import { client } from '@api/rest/client';
import { toastErrorTranslated, toastSuccessTranslated } from '@helpers/toast';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useMainSelector } from '../useMainSelector';
import { useDataSourcesQueryParams } from './useDataSourcesQueryParams';

export const useDataSourceFolderActions = () => {
  const dispatch = useDispatch();
  const { dataSources } = useMainSelector();

  const { setSubfolderFromFolder } = useDataSourcesQueryParams();

  const fetchDataSourceFolders = useCallback(async () => {
    try {
      const { data } =
        await client.dataSourceFolders.getAllDataSourceFoldersWithSubfolders();
      dispatch(mainActions.setDataSourceFolders(data || []));
    } catch (error) {
      console.error(error);
      toastErrorTranslated(error);
    }
  }, [dispatch]);

  const createSubFolder = useCallback(
    async (parentFolderId: string, name: string, description?: string) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data } =
          await client.dataSourceSubfolders.createDataSourceSubfolder({
            body: {
              parentFolderId,
              name,
              description,
            },
          });
        dispatch(mainActions.setDataSourceFolders(data || []));
        toastSuccessTranslated('data-sources.subfolder-created.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const deleteSubFolder = useCallback(
    async (folderId: string) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const { data } =
          await client.dataSourceSubfolders.deleteDataSourceSubfolder({
            params: { dataSourceSubfolderId: folderId },
          });
        dispatch(mainActions.setDataSourceFolders(data || []));
        toastSuccessTranslated('data-sources.subfolder-deleted.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const handleSubfolderClick = useCallback(
    (subfolderName: string) => {
      setSubfolderFromFolder(subfolderName, dataSources.selectedFolder);
    },
    [dataSources.selectedFolder, setSubfolderFromFolder],
  );

  return {
    fetchDataSourceFolders,
    createSubFolder,
    deleteSubFolder,
    handleSubfolderClick,
  };
};
